import { auditService } from './auditService.js';
import { EntityValidationError, } from '../validators/types.js';
import { buildAuditSnapshot, diffForAudit, } from './fieldAudit/index.js';
import { logCrudActivity, safeChangedFieldNames } from '../log/activity.js';
/**
 * Base CRUD for subcollection (child) entities whose repository is already
 * resolved with parent context (e.g. `{ orgId, technicalSheetId }`).
 *
 * Write funnel matches {@link OrgScopedCrudService}: validate → audit → repository
 * (or enqueue on {@link WriteBatch} when `batch` is set). Spec 0045 (concurrent
 * edit / `version`) hooks into the private writers below — do not scatter version
 * checks into subclasses.
 *
 * Field audit (spec 0046 / request 0250–0255): {@link diffSave} / {@link deleteAll}
 * and standalone {@link create} / {@link update} / {@link delete} when
 * `audited: true` **and** an {@link AggregateAuditScope} is passed.
 */
export class ChildCrudService {
    constructor(repository, opts) {
        this.repository = repository;
        this.opts = opts;
    }
    async getAll(sort) {
        const effective = sort ?? this.opts.defaultSort;
        return this.repository.getAll(effective
            ? { sort: { field: effective.field, direction: effective.direction } }
            : undefined);
    }
    /**
     * Clears the repository collection cache (org override may re-enable caching
     * even when the descriptor says NoCache), then reads via {@link getAll}.
     * Use for dialog Recarregar / initial loads that must not serve stale children.
     */
    async getAllFresh(sort) {
        await this.repository.invalidateCache();
        return this.getAll(sort);
    }
    async getById(id) {
        return this.repository.getById(id);
    }
    /**
     * Private create writer — single extension point for validate → audit → persist.
     * Spec 0045 installs `version` conflict detection here (before repository.create).
     */
    async writeCreate(data, writeOpts) {
        if (!writeOpts?.skipValidation && this.opts.validate) {
            const errors = this.opts.validate(data);
            if (errors && Object.keys(errors).length > 0)
                throw new EntityValidationError(errors);
        }
        let managed = { ...data };
        if (this.opts.auditable !== false) {
            managed = auditService.stampOnCreate(managed);
        }
        // 0045: version conflict check lands here (before repository.create).
        if (writeOpts?.batch) {
            const id = writeOpts.batch.create(this.repository, managed);
            return { ...managed, id };
        }
        return this.repository.create(managed);
    }
    /**
     * Private update writer — single extension point for validate → audit → persist.
     * Spec 0045 installs `version` conflict detection here (before repository.update).
     */
    async writeUpdate(id, data, writeOpts) {
        if (!writeOpts?.skipValidation && this.opts.validate) {
            const errors = this.opts.validate(data);
            if (errors && Object.keys(errors).length > 0)
                throw new EntityValidationError(errors);
        }
        let managed = { ...data };
        if (this.opts.auditable !== false) {
            managed = auditService.stampOnUpdate(managed);
        }
        // 0045: version conflict check lands here (before repository.update).
        if (writeOpts?.batch) {
            writeOpts.batch.update(this.repository, id, managed);
            return { ...managed, id };
        }
        return this.repository.update(id, managed);
    }
    /**
     * Single registrar for child field-audit ops (diffSave + standalone methods).
     * Call **after** the matching write is enqueued/persisted.
     */
    recordChildAudit(audit, input) {
        audit.noteDestination(this.repository);
        if (input.op === 'delete') {
            audit.recorder.record({
                entity: this.opts.entity,
                recordId: input.recordId,
                op: 'delete',
                changes: [],
                snapshot: buildAuditSnapshot(input.before, this.opts.entity) ??
                    undefined,
                aggregate: audit.aggregate,
            });
            return;
        }
        if (input.op === 'create') {
            const changes = diffForAudit(null, input.payload, this.opts.entity);
            audit.recorder.record({
                entity: this.opts.entity,
                recordId: input.recordId,
                op: 'create',
                changes,
                snapshot: buildAuditSnapshot({ ...input.payload, id: input.recordId }, this.opts.entity) ?? undefined,
                aggregate: audit.aggregate,
            });
            return;
        }
        const changes = diffForAudit(input.before, input.payload, this.opts.entity);
        audit.recorder.record({
            entity: this.opts.entity,
            recordId: input.recordId,
            op: 'update',
            changes,
            snapshot: buildAuditSnapshot({
                ...input.before,
                ...input.payload,
                id: input.recordId,
            }, this.opts.entity) ?? undefined,
            aggregate: audit.aggregate,
        });
    }
    assertBeforeForAudit(method, opts) {
        if (this.opts.audited && opts?.audit && opts.before === undefined) {
            throw new Error(`${this.opts.entity}: ${method} with AggregateAuditScope requires before`);
        }
    }
    async create(data, opts) {
        const started = Date.now();
        const batched = Boolean(opts?.batch);
        try {
            const created = await this.writeCreate(data, opts);
            if (this.opts.audited && opts?.audit) {
                this.recordChildAudit(opts.audit, {
                    op: 'create',
                    recordId: created.id,
                    payload: data,
                });
            }
            // Batch has no post-commit hook — emit now with batched:true (0352).
            logCrudActivity({
                action: 'record.create',
                entity: this.opts.entity,
                entityId: created.id,
                record: created,
                durationMs: Date.now() - started,
                batched,
            });
            return created;
        }
        catch (error) {
            logCrudActivity({
                action: 'record.create',
                entity: this.opts.entity,
                record: data,
                outcome: 'error',
                durationMs: Date.now() - started,
                batched,
                error,
            });
            throw error;
        }
    }
    async update(id, data, opts) {
        const started = Date.now();
        const batched = Boolean(opts?.batch);
        const fields = opts?.before
            ? safeChangedFieldNames(opts.before, data, this.opts.entity)
            : undefined;
        const recordForLabel = opts?.before
            ? { ...opts.before, ...data, id }
            : { ...data, id };
        try {
            this.assertBeforeForAudit('update', opts);
            const updated = await this.writeUpdate(id, data, opts);
            if (this.opts.audited && opts?.audit) {
                this.recordChildAudit(opts.audit, {
                    op: 'update',
                    recordId: id,
                    before: opts.before,
                    payload: data,
                });
            }
            logCrudActivity({
                action: 'record.update',
                entity: this.opts.entity,
                entityId: id,
                record: recordForLabel,
                durationMs: Date.now() - started,
                fields,
                batched,
            });
            return updated;
        }
        catch (error) {
            logCrudActivity({
                action: 'record.update',
                entity: this.opts.entity,
                entityId: id,
                record: recordForLabel,
                outcome: 'error',
                durationMs: Date.now() - started,
                fields,
                batched,
                error,
            });
            throw error;
        }
    }
    async delete(id, opts) {
        const started = Date.now();
        const batched = Boolean(opts?.batch);
        const recordForLabel = opts?.before ?? { id };
        try {
            this.assertBeforeForAudit('delete', opts);
            let ok;
            if (opts?.batch) {
                opts.batch.delete(this.repository, id);
                ok = true;
            }
            else {
                ok = await this.repository.delete(id);
            }
            if (this.opts.audited && opts?.audit) {
                this.recordChildAudit(opts.audit, {
                    op: 'delete',
                    recordId: id,
                    before: opts.before,
                });
            }
            logCrudActivity({
                action: 'record.delete',
                entity: this.opts.entity,
                entityId: id,
                record: recordForLabel,
                durationMs: Date.now() - started,
                batched,
            });
            return ok;
        }
        catch (error) {
            logCrudActivity({
                action: 'record.delete',
                entity: this.opts.entity,
                entityId: id,
                record: recordForLabel,
                outcome: 'error',
                durationMs: Date.now() - started,
                batched,
                error,
            });
            throw error;
        }
    }
    /**
     * Deletes every row. When field-auditing, entries are recorded **after** each
     * delete is enqueued/persisted so a mid-loop failure cannot leave orphan log
     * rows without a matching write (request 0250 trap 2).
     */
    async deleteAll(opts) {
        const all = await this.repository.getAll();
        for (const item of all) {
            await this.delete(item.id, {
                batch: opts?.batch,
                audit: opts?.audit,
                before: item,
            });
        }
    }
    /**
     * Shared diff-batch. Not named `saveRows` — that name is already public on
     * technicalSheetOverrideInputService / ProcessService.
     *
     * Semantics: update rows that existed in `original`, create the rest, delete
     * originals absent from `items`. Operation order is fixed — do not reorder.
     *
     * Field audit uses `original` as the before-image (no extra read — request 0250 §4.3).
     * Child create/update also store a post-save {@link buildAuditSnapshot} so the
     * history UI can identify the row without re-reading it (request 0251).
     */
    async diffSave(items, original, toPayload, opts) {
        const existingIds = new Set(original.map((r) => r.id));
        const originalById = new Map(original.map((r) => [r.id, r]));
        const processedIds = new Set();
        const writeOpts = {
            skipValidation: opts?.skipItemValidation ? true : undefined,
            batch: opts?.batch,
        };
        const shouldAudit = Boolean(this.opts.audited && opts?.audit);
        const audit = opts?.audit;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const payload = toPayload(item, i);
            if (item.id && existingIds.has(item.id)) {
                await this.writeUpdate(item.id, payload, writeOpts);
                processedIds.add(item.id);
                if (shouldAudit && audit) {
                    this.recordChildAudit(audit, {
                        op: 'update',
                        recordId: item.id,
                        before: originalById.get(item.id),
                        payload,
                    });
                }
            }
            else {
                // Caller-supplied id (generateWriteBatchId before the batch) must survive
                // toPayload, which only carries business fields. Empty id → batch generates one.
                const payloadWithId = item.id ? { ...payload, id: item.id } : payload;
                const created = await this.writeCreate(payloadWithId, writeOpts);
                processedIds.add(created.id);
                if (shouldAudit && audit) {
                    this.recordChildAudit(audit, {
                        op: 'create',
                        recordId: created.id,
                        payload,
                    });
                }
            }
        }
        for (const orig of original) {
            if (!processedIds.has(orig.id)) {
                await this.delete(orig.id, {
                    batch: opts?.batch,
                    // Pass before without audit here — we record below via the shared
                    // registrar so deleteAll / diffSave share one path without double-count.
                });
                if (shouldAudit && audit) {
                    this.recordChildAudit(audit, {
                        op: 'delete',
                        recordId: orig.id,
                        before: orig,
                    });
                }
            }
        }
    }
}
//# sourceMappingURL=ChildCrudService.js.map